import { useState } from "react";
import {
    useUndismissedNotifications,
    useMarkNotificationsAsDismissed,
    useMarkNotificationsAsRead,
    useDeleteNotification,
} from "@/hooks/use-user-notifications";
import { NotificationDto } from "@/api";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from "react-i18next";
import { Bell, CheckCheck, Trash2 } from "lucide-react";
import { NotificationCard } from "@/components/notification-card";

export function NotificationPopup() {
    const { t } = useTranslation();
    const { data: notifications, isLoading } = useUndismissedNotifications();
    const markAsDismissedMutation = useMarkNotificationsAsDismissed();
    const markAsReadMutation = useMarkNotificationsAsRead();
    const deleteNotificationMutation = useDeleteNotification();

    const [isSnoozed, setIsSnoozed] = useState(false);

    const hasNotifications =
        Array.isArray(notifications) && notifications.length > 0;
    const isOpen = !isLoading && hasNotifications && !isSnoozed;

    const handleDismissAll = () => {
        if (!Array.isArray(notifications) || !notifications.length) return;

        markAsDismissedMutation.mutate(
            {
                notificationIds: notifications.map(
                    (n: NotificationDto) => n.id,
                ),
            },
            {
                onSuccess: () => {
                    setIsSnoozed(false);
                },
            },
        );
    };

    const handleMarkAsRead = (id: number) => {
        markAsReadMutation.mutate({ notificationIds: [id] });
    };

    const handleDelete = (id: number) => {
        deleteNotificationMutation.mutate(id);
    };

    if (!hasNotifications || !Array.isArray(notifications)) return null;

    return (
        <Dialog
            onOpenChange={(open) => {
                if (!open) setIsSnoozed(true);
            }}
            open={isOpen}
        >
            <DialogContent className="max-w-2xl max-h-[80vh]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Bell className="h-5 w-5" />
                        {t("notifications.popup.title")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("notifications.popup.description", {
                            count: notifications.length,
                        })}
                    </DialogDescription>
                </DialogHeader>

                <ScrollArea className="max-h-[50vh] pr-4">
                    <div className="space-y-4">
                        {notifications.map((notification: NotificationDto) => (
                            <div
                                className="border rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                key={notification.id}
                            >
                                <NotificationCard
                                    notification={notification}
                                    onActionClick={handleDismissAll}
                                >
                                    <div className="mt-3 pt-3 border-t flex w-full gap-2">
                                        <Button
                                            className="flex-1 gap-2"
                                            disabled={
                                                deleteNotificationMutation.isPending
                                            }
                                            onClick={() =>
                                                handleDelete(notification.id)
                                            }
                                            size="sm"
                                            variant="destructive"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            {t("common.delete")}
                                        </Button>
                                        {!notification.isRead ? (
                                            <Button
                                                className="flex-1 gap-2"
                                                disabled={
                                                    markAsReadMutation.isPending
                                                }
                                                onClick={() =>
                                                    handleMarkAsRead(
                                                        notification.id,
                                                    )
                                                }
                                                size="sm"
                                                variant="outline"
                                            >
                                                <CheckCheck className="h-4 w-4" />
                                                {t(
                                                    "notifications.mark-as-read",
                                                )}
                                            </Button>
                                        ) : null}
                                    </div>
                                </NotificationCard>
                            </div>
                        ))}
                    </div>
                </ScrollArea>

                <DialogFooter>
                    <Button
                        disabled={markAsDismissedMutation.isPending}
                        onClick={handleDismissAll}
                    >
                        {t("notifications.popup.dismiss-all")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
